/**
 * Float samples in, the voice service's one accepted format out: 16 kHz mono 16-bit PCM
 * WAV. Built here by hand, never taken from MediaRecorder, whose container bytes v3 once
 * uploaded as samples and transcribed as noise on every turn.
 */

export const WAV_RATE = 16000;

const HEADER_BYTES = 44;

/** Linear interpolation, from the microphone's own rate (44.1 or 48 kHz as a rule).
 *  Speech has little above the 8 kHz it would alias. */
function resample(samples: Float32Array, from: number): Float32Array {
  if (from === WAV_RATE) return samples;
  const step = from / WAV_RATE;
  const out = new Float32Array(Math.floor(samples.length / step));
  for (let i = 0; i < out.length; i++) {
    const at = i * step;
    const j = Math.floor(at);
    const next = samples[Math.min(j + 1, samples.length - 1)];
    out[i] = samples[j] + (next - samples[j]) * (at - j);
  }
  return out;
}

export function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const pcm = resample(samples, sampleRate);
  const buf = new ArrayBuffer(HEADER_BYTES + pcm.length * 2);
  const v = new DataView(buf);
  const ascii = (at: number, s: string) =>
    [...s].forEach((c, i) => v.setUint8(at + i, c.charCodeAt(0)));

  ascii(0, "RIFF");
  v.setUint32(4, buf.byteLength - 8, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, WAV_RATE, true);
  v.setUint32(28, WAV_RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  ascii(36, "data");
  v.setUint32(40, pcm.length * 2, true);

  pcm.forEach((s, i) => {
    const c = Math.max(-1, Math.min(1, s));
    v.setInt16(HEADER_BYTES + 2 * i, Math.round(c < 0 ? c * 32768 : c * 32767), true);
  });
  return buf;
}
